import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { Button } from 'primereact/button';

interface BranchData {
  name: string;
  actualValue: number;
  expectedValue: number;
}

interface FinancialChartProps {
  data: BranchData[];
  title: string;
  actualLabel: string;
  expectedLabel: string;
  actualColor: string;
  expectedColor: string;
  onFilterClick?: () => void;
}

export const FinancialChart: React.FC<FinancialChartProps> = ({
  data,
  title,
  actualLabel,
  expectedLabel,
  actualColor,
  expectedColor,
  onFilterClick,
}) => {
  // Check if dark mode is active
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Check if dark mode class is applied to body
    const checkDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark-mode'));
    };

    // Initial check
    checkDarkMode();

    // Set up observer to detect changes to body class
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });

    // Start observing
    observer.observe(document.body, { attributes: true });

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, []);

  const chartData = {
    labels: data.map((branch) => branch.name),
    datasets: [
      {
        label: actualLabel,
        data: data.map((branch) => branch.actualValue),
        backgroundColor: actualColor,
        borderColor: actualColor,
        borderWidth: 1,
      },
      {
        label: expectedLabel,
        data: data.map((branch) => branch.expectedValue),
        backgroundColor: expectedColor,
        borderColor: expectedColor,
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        padding: 5,
        labels: {
          padding: 5,
          boxWidth: 12,
          color: isDarkMode ? '#e0e0e0' : '#1e293b',
        },
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 16,
          weight: 'bold',
          color: isDarkMode ? '#e0e0e0' : '#1e293b',
        },
        padding: {
          top: 5,
          bottom: 5,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value: number) {
            return '₪' + value.toLocaleString();
          },
          padding: 5,
          color: isDarkMode ? '#e0e0e0' : '#1e293b',
        },
        grid: {
          drawBorder: false,
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 11,
          },
          autoSkip: false,
          maxTicksLimit: 10,
          padding: 5,
          color: isDarkMode ? '#e0e0e0' : '#1e293b',
        },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
    },
    layout: {
      padding: 0,
    },
    barPercentage: 1,
    categoryPercentage: 0.9,
    barThickness: 'flex',
  };

  const pieData = {
    labels: [actualLabel, expectedLabel],
    datasets: [
      {
        data: [
          data.reduce((sum, branch) => sum + branch.actualValue, 0),
          data.reduce((sum, branch) => sum + branch.expectedValue, 0),
        ],
        backgroundColor: [actualColor, expectedColor],
        borderColor: [actualColor, expectedColor],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'center' as const,
        padding: 5,
        labels: {
          boxWidth: 12,
          padding: 10,
          font: {
            size: 11,
          },
          color: isDarkMode ? '#e0e0e0' : '#1e293b',
        },
      },
      title: {
        display: true,
        text: `סך הכל ${title} `,
        font: {
          size: 14,
          weight: 'bold',
          color: isDarkMode ? '#e0e0e0' : '#1e293b',
        },
        padding: {
          top: 5,
          bottom: 5,
        },
      },
    },
    layout: {
      padding: {
        left: 5,
        right: 5,
        top: 5,
        bottom: 5,
      },
    },
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h2 style={{ margin: 0 }}>{title}</h2>
        {onFilterClick && (
          <Button
            icon="pi pi-filter"
            label="סינון"
            size="small"
            outlined
            onClick={onFilterClick}
            style={{ fontSize: '12px', padding: '6px 12px' }}
          />
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
        <div
          style={{ width: '100%', height: '100%' }}
          className="chart-card bar-chart-card"
        >
          <Chart
            type="bar"
            data={chartData}
            options={chartOptions}
            height="300px"
            width={'100%'}
          />
        </div>
        <div style={{ width: '300px', padding: '0px' }}>
          <Chart
            type="pie"
            data={pieData}
            options={pieOptions}
            height="300px"
            width={'100%'}
          />
        </div>
      </div>
    </div>
  );
};
